// =============================================================================
// CALCULS DE RENTABILITÉ - TOUT EN HT
// =============================================================================

/**
 * RÈGLE FONDAMENTALE :
 * Tous les calculs de rentabilité sont effectués en HT (Hors Taxes).
 * La TVA est neutre pour l'entreprise (collectée puis reversée à l'État).
 *
 * MARGE = CA HT - DÉPENSES HT
 * Où DÉPENSES HT = Factures fournisseurs HT + Frais HT + Main d'œuvre (pas de TVA)
 *
 * SEUILS DYNAMIQUES PAR MÉTIER :
 * - Métiers GÉNÉRALISTES (maçon, TCE, rénovateur, etc.) → SEUILS_MARGE génériques
 * - Métiers SPÉCIALISÉS (plombier, électricien, etc.) → Seuils Cerfrance spécifiques
 */

import {
  getSeuilsMargeMetier,
  isMetierGeneraliste,
  getBenchmarkMetier,
  type SeuilsMarge,
} from '@/data/benchmarkMetiers';

// =============================================================================
// CONSTANTES TVA
// =============================================================================

export const TVA_STANDARD = 20;
export const TVA_RENOVATION = 10;
export const TVA_TRAVAUX_AMELIORATION = 5.5;

// Types de frais généralement sans TVA
export const FRAIS_SANS_TVA = [
  'Assurance',
  'Assurances',
  'Salaires',
  'Main d\'œuvre',
  'Intérêts',
  'Frais bancaires',
  'Amendes',
  'Taxes locales',
];

// =============================================================================
// SEUILS DE RENTABILITÉ HARMONISÉS BTP FRANCE (À UTILISER PARTOUT)
// Basé sur les études FFB, Bpifrance 2024
// =============================================================================

export const SEUILS_MARGE = {
  EXCEPTIONNEL: 28,  // >= 28% : Top 10% du secteur BTP
  EXCELLENT: 22,     // >= 22% : Au-dessus moyenne BTP
  BON: 18,           // >= 18% : Moyenne BTP
  CORRECT: 12,       // >= 12% : En dessous moyenne
  FRAGILE: 5,        // >= 5%  : Problématique
  CRITIQUE: 0,       // >= 0%  : Action urgente
  // < 0% = DÉFICIT
} as const;

// Alias pour compatibilité avec l'ancien code
export const SEUILS_RENTABILITE = {
  OPTIMALE: SEUILS_MARGE.EXCEPTIONNEL, // 28%
  BONNE: SEUILS_MARGE.BON,             // 18%
  CORRECTE: SEUILS_MARGE.CORRECT,      // 12%
  FRAGILE: SEUILS_MARGE.CRITIQUE,      // 0%
  DEFICIT: -Infinity,
} as const;

export const BENCHMARKS_BTP = {
  marge_min: 18,       // Seuil minimum acceptable (moyenne BTP)
  marge_max: 28,       // Seuil excellence (top 10%)
  marge_moyenne: 22,   // Moyenne nationale BTP
  marge_median: 20,    // Médiane du secteur
} as const;

// Moyenne BTP nationale - constante utilitaire
export const MARGE_MOYENNE_BTP = 22;

// =============================================================================
// TYPES
// =============================================================================

export type StatutRentabilite = 'VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE';

export interface DonneesChantier {
  devisHT: number;
  facturesFournisseursHT: number;
  fraisHT: number;
  mainOeuvre: number; // Pas de TVA sur la MO
}

export interface ResultatRentabilite {
  caHT: number;
  depensesTotalHT: number;
  margeEuros: number;
  margePct: number;
  roi: number;
  statut: StatutRentabilite;
}

export interface MargeConfig {
  label: string;
  labelCourt: string;
  color: string;
  bgColor: string;
  status: StatutRentabilite;
  description: string;
  emoji: string;
}

// =============================================================================
// FONCTIONS DE CONVERSION TVA
// =============================================================================

/**
 * Calcule le montant HT à partir du TTC
 */
export function ttcToHt(montantTTC: number, tauxTVA: number = TVA_STANDARD): number {
  return Math.round((montantTTC / (1 + tauxTVA / 100)) * 100) / 100;
}

/**
 * Calcule le montant TTC à partir du HT
 */
export function htToTtc(montantHT: number, tauxTVA: number = TVA_STANDARD): number {
  return Math.round((montantHT * (1 + tauxTVA / 100)) * 100) / 100;
}

/**
 * Calcule la TVA
 */
export function calculerTVA(montantHT: number, tauxTVA: number = TVA_STANDARD): number {
  return Math.round((montantHT * tauxTVA / 100) * 100) / 100;
}

/**
 * Détermine si un type de frais est généralement soumis à TVA
 */
export function fraisSoumisTVA(typeFrais: string): boolean {
  return !FRAIS_SANS_TVA.some(f =>
    typeFrais.toLowerCase().includes(f.toLowerCase())
  );
}

// =============================================================================
// CALCULS DE RENTABILITÉ (TOUT EN HT)
// =============================================================================

/**
 * Détermine le statut de rentabilité selon les seuils harmonisés BTP
 * VERT: >= 18% (moyenne BTP)
 * JAUNE: >= 12%
 * ORANGE: >= 0%
 * ROUGE: < 0% (déficit)
 */
export function getStatutRentabilite(margePct: number): StatutRentabilite {
  if (margePct >= SEUILS_MARGE.BON) return 'VERT';       // >= 18%
  if (margePct >= SEUILS_MARGE.CORRECT) return 'JAUNE';  // >= 12%
  if (margePct >= SEUILS_MARGE.CRITIQUE) return 'ORANGE'; // >= 0%
  return 'ROUGE'; // < 0% = déficit
}

/**
 * Alias pour getStatutRentabilite - utilisé par pdfStyles et usePDFData
 */
export const getMargeStatus = getStatutRentabilite;

/**
 * Calcule la rentabilité d'un chantier (TOUT EN HT)
 */
export function calculerRentabilite(donnees: DonneesChantier): ResultatRentabilite {
  const { devisHT, facturesFournisseursHT, fraisHT, mainOeuvre } = donnees;

  // Toutes les dépenses en HT
  const depensesTotalHT = facturesFournisseursHT + fraisHT + mainOeuvre;

  // Marge = CA HT - Dépenses HT
  const margeEuros = devisHT - depensesTotalHT;

  // Taux de marge = Marge / CA HT × 100
  const margePct = devisHT > 0 ? (margeEuros / devisHT) * 100 : 0;

  // ROI = Marge / Dépenses × 100
  const roi = depensesTotalHT > 0 ? (margeEuros / depensesTotalHT) * 100 : 0;

  return {
    caHT: devisHT,
    depensesTotalHT,
    margeEuros: Math.round(margeEuros * 100) / 100,
    margePct: Math.round(margePct * 100) / 100,
    roi: Math.round(roi * 100) / 100,
    statut: getStatutRentabilite(margePct),
  };
}

/**
 * Calcule le CA par jour (en HT)
 */
export function calculerCAParJour(devisHT: number, dureeJours: number): number {
  if (dureeJours <= 0) return 0;
  return Math.round(devisHT / dureeJours);
}

/**
 * Calcule la rentabilité horaire (en HT)
 */
export function calculerRentabiliteHoraire(margeEuros: number, heuresTravaillees: number): number {
  if (heuresTravaillees <= 0) return 0;
  return Math.round((margeEuros / heuresTravaillees) * 100) / 100;
}

/**
 * Calcule le coût journalier de l'équipe
 */
export function calculerCoutJournalier(coutMO: number, dureeJours: number): number {
  if (dureeJours <= 0) return 0;
  return Math.round(coutMO / dureeJours);
}

/**
 * Calcule le budget disponible (en HT)
 */
export function calculerBudgetDisponible(devisHT: number, depensesHT: number): number {
  return Math.round((devisHT - depensesHT) * 100) / 100;
}

/**
 * Calcule le jour critique (quand la marge devient nulle)
 */
export function calculerJourCritique(
  devisHT: number,
  depensesFixesHT: number,
  coutJournalierEquipe: number
): number | null {
  if (coutJournalierEquipe <= 0) return null;
  const budgetRestant = devisHT - depensesFixesHT;
  if (budgetRestant <= 0) return 0;
  return Math.floor(budgetRestant / coutJournalierEquipe);
}

// =============================================================================
// CONFIGURATION DES BADGES DE MARGE - 7 NIVEAUX BTP
// =============================================================================

/**
 * Retourne la configuration du badge de marge selon le taux
 * 7 niveaux basés sur les benchmarks BTP France (FFB, Bpifrance 2024)
 */
export function getMargeConfig(tauxMarge: number): MargeConfig {
  if (tauxMarge >= SEUILS_MARGE.EXCEPTIONNEL) { // >= 28%
    return {
      label: 'RENTABILITÉ EXCEPTIONNELLE',
      labelCourt: 'EXCEPTIONNEL',
      color: '#047857',
      bgColor: '#D1FAE5',
      status: 'VERT',
      description: 'Top 10% du secteur BTP',
      emoji: '🏆'
    };
  }
  if (tauxMarge >= SEUILS_MARGE.EXCELLENT) { // >= 22%
    return {
      label: 'EXCELLENTE RENTABILITÉ',
      labelCourt: 'EXCELLENT',
      color: '#10B981',
      bgColor: '#D1FAE5',
      status: 'VERT',
      description: 'Au-dessus de la moyenne BTP (22%)',
      emoji: '✅'
    };
  }
  if (tauxMarge >= SEUILS_MARGE.BON) { // >= 18%
    return {
      label: 'BONNE RENTABILITÉ',
      labelCourt: 'BON',
      color: '#34D399',
      bgColor: '#ECFDF5',
      status: 'VERT',
      description: 'Dans la moyenne BTP',
      emoji: '👍'
    };
  }
  if (tauxMarge >= SEUILS_MARGE.CORRECT) { // >= 12%
    return {
      label: 'RENTABILITÉ CORRECTE',
      labelCourt: 'CORRECT',
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      status: 'JAUNE',
      description: 'En dessous de la moyenne, à surveiller',
      emoji: '⚠️'
    };
  }
  if (tauxMarge >= SEUILS_MARGE.FRAGILE) { // >= 5%
    return {
      label: 'RENTABILITÉ FRAGILE',
      labelCourt: 'FRAGILE',
      color: '#D97706',
      bgColor: '#FEF3C7',
      status: 'ORANGE',
      description: 'Marge faible, optimisation nécessaire',
      emoji: '🟠'
    };
  }
  if (tauxMarge >= SEUILS_MARGE.CRITIQUE) { // >= 0%
    return {
      label: 'RENTABILITÉ CRITIQUE',
      labelCourt: 'CRITIQUE',
      color: '#EA580C',
      bgColor: '#FED7AA',
      status: 'ORANGE',
      description: 'Action urgente requise',
      emoji: '🚨'
    };
  }
  // < 0% = DÉFICIT
  return {
    label: 'DÉFICIT',
    labelCourt: 'DÉFICIT',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    status: 'ROUGE',
    description: 'Chantier à perte',
    emoji: '❌'
  };
}

// =============================================================================
// FORMATAGE POUR AFFICHAGE
// =============================================================================

/**
 * Formate un montant avec indication HT
 */
export function formatMontantHT(montant: number): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(montant);
  // Remplacer l'espace insécable par un espace normal pour PDF
  return formatted.replace(/\u202F/g, ' ') + ' € HT';
}

/**
 * Formate un montant avec indication TTC
 */
export function formatMontantTTC(montant: number): string {
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(montant);
  return formatted.replace(/\u202F/g, ' ') + ' € TTC';
}

/**
 * Formate un montant compact (1.5k €)
 */
export function formatMontantCompact(montant: number, showHT = true): string {
  if (Math.abs(montant) >= 1000) {
    const compact = (montant / 1000).toFixed(1).replace('.', ',') + 'k €';
    return showHT ? compact + ' HT' : compact;
  }
  return showHT ? formatMontantHT(montant) : montant.toLocaleString('fr-FR') + ' €';
}

/**
 * Formate un montant complet avec HT et TTC
 */
export function formatMontantComplet(montantHT: number, tauxTVA: number = TVA_STANDARD): string {
  const ttc = htToTtc(montantHT, tauxTVA);
  const htFormatted = new Intl.NumberFormat('fr-FR').format(montantHT).replace(/\u202F/g, ' ');
  const ttcFormatted = new Intl.NumberFormat('fr-FR').format(ttc).replace(/\u202F/g, ' ');
  return `${htFormatted} € HT (${ttcFormatted} € TTC)`;
}

/**
 * Formate un pourcentage
 */
export function formatPourcentage(value: number, showSign = false): string {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

// =============================================================================
// SEUILS DYNAMIQUES PAR MÉTIER
// =============================================================================

/**
 * Récupère les seuils de marge adaptés au métier de l'entreprise
 *
 * - Métiers GÉNÉRALISTES (maçon, TCE, etc.) → SEUILS_MARGE génériques BTP
 * - Métiers SPÉCIALISÉS (plombier, électricien, etc.) → Seuils Cerfrance
 *
 * @param metierKey - Clé du métier (ex: 'plombier', 'macon', 'autre')
 * @returns Seuils de marge adaptés au métier
 */
export function getSeuilsParMetier(metierKey: string | undefined): SeuilsMarge {
  // Seuils génériques BTP (fallback)
  const seuilsGeneriques: SeuilsMarge = {
    exceptionnel: SEUILS_MARGE.EXCEPTIONNEL,
    excellent: SEUILS_MARGE.EXCELLENT,
    bon: SEUILS_MARGE.BON,
    correct: SEUILS_MARGE.CORRECT,
    fragile: SEUILS_MARGE.FRAGILE,
    critique: SEUILS_MARGE.CRITIQUE,
  };

  // Si pas de métier ou métier généraliste, retourner les seuils génériques
  if (!metierKey || isMetierGeneraliste(metierKey)) {
    return seuilsGeneriques;
  }

  // Sinon, retourner les seuils spécifiques du métier (ou fallback si null)
  const seuilsMetier = getSeuilsMargeMetier(metierKey);
  return seuilsMetier ?? seuilsGeneriques;
}

/**
 * Détermine le statut de rentabilité selon les seuils du métier
 *
 * @param margePct - Taux de marge en pourcentage
 * @param metierKey - Clé du métier (optionnel, utilise seuils génériques si non fourni)
 * @returns StatutRentabilite ('VERT' | 'JAUNE' | 'ORANGE' | 'ROUGE')
 */
export function getStatutRentabiliteParMetier(
  margePct: number,
  metierKey?: string
): StatutRentabilite {
  const seuils = getSeuilsParMetier(metierKey);

  if (margePct >= seuils.bon) return 'VERT';
  if (margePct >= seuils.correct) return 'JAUNE';
  if (margePct >= seuils.critique) return 'ORANGE';
  return 'ROUGE';
}

/**
 * Retourne la configuration du badge de marge selon le métier
 *
 * @param tauxMarge - Taux de marge en pourcentage
 * @param metierKey - Clé du métier (optionnel)
 * @returns Configuration du badge avec couleurs et labels adaptés
 */
export function getMargeConfigParMetier(
  tauxMarge: number,
  metierKey?: string
): MargeConfig {
  const seuils = getSeuilsParMetier(metierKey);
  const metier = metierKey ? getBenchmarkMetier(metierKey) : null;
  const metierName = metier?.metier_name || 'BTP';

  if (tauxMarge >= seuils.excellent) {
    return {
      label: 'RENTABILITÉ EXCEPTIONNELLE',
      labelCourt: 'EXCEPTIONNEL',
      color: '#047857',
      bgColor: '#D1FAE5',
      status: 'VERT',
      description: `Top performance ${metierName}`,
      emoji: '🏆'
    };
  }
  if (tauxMarge >= seuils.bon) {
    return {
      label: 'EXCELLENTE RENTABILITÉ',
      labelCourt: 'EXCELLENT',
      color: '#10B981',
      bgColor: '#D1FAE5',
      status: 'VERT',
      description: `Objectif atteint pour ${metierName}`,
      emoji: '✅'
    };
  }
  if (tauxMarge >= seuils.correct) {
    return {
      label: 'BONNE RENTABILITÉ',
      labelCourt: 'BON',
      color: '#34D399',
      bgColor: '#ECFDF5',
      status: 'VERT',
      description: `Dans la moyenne ${metierName}`,
      emoji: '👍'
    };
  }
  if (tauxMarge >= seuils.fragile) {
    return {
      label: 'RENTABILITÉ CORRECTE',
      labelCourt: 'CORRECT',
      color: '#F59E0B',
      bgColor: '#FEF3C7',
      status: 'JAUNE',
      description: `En dessous de la moyenne ${metierName}`,
      emoji: '⚠️'
    };
  }
  if (tauxMarge >= seuils.critique) {
    return {
      label: 'RENTABILITÉ FRAGILE',
      labelCourt: 'FRAGILE',
      color: '#D97706',
      bgColor: '#FEF3C7',
      status: 'ORANGE',
      description: `Marge faible pour ${metierName}`,
      emoji: '🟠'
    };
  }
  if (tauxMarge >= 0) {
    return {
      label: 'RENTABILITÉ CRITIQUE',
      labelCourt: 'CRITIQUE',
      color: '#EA580C',
      bgColor: '#FED7AA',
      status: 'ORANGE',
      description: 'Action urgente requise',
      emoji: '🚨'
    };
  }
  return {
    label: 'DÉFICIT',
    labelCourt: 'DÉFICIT',
    color: '#EF4444',
    bgColor: '#FEE2E2',
    status: 'ROUGE',
    description: 'Chantier à perte',
    emoji: '❌'
  };
}

/**
 * Compare la marge actuelle aux benchmarks du métier
 *
 * @param margePct - Taux de marge actuel
 * @param metierKey - Clé du métier
 * @returns Analyse comparative
 */
export function comparerMargeAuBenchmarkMetier(
  margePct: number,
  metierKey?: string
): {
  niveau: 'excellent' | 'bon' | 'correct' | 'fragile' | 'critique' | 'deficit';
  ecartVsCible: number;
  ecartVsMinimum: number;
  message: string;
} {
  const seuils = getSeuilsParMetier(metierKey);
  const metier = metierKey ? getBenchmarkMetier(metierKey) : null;
  const metierName = metier?.metier_name || 'BTP';

  let niveau: 'excellent' | 'bon' | 'correct' | 'fragile' | 'critique' | 'deficit';
  if (margePct >= seuils.excellent) niveau = 'excellent';
  else if (margePct >= seuils.bon) niveau = 'bon';
  else if (margePct >= seuils.correct) niveau = 'correct';
  else if (margePct >= seuils.fragile) niveau = 'fragile';
  else if (margePct >= seuils.critique) niveau = 'critique';
  else niveau = 'deficit';

  const ecartVsCible = margePct - seuils.bon;
  const ecartVsMinimum = margePct - seuils.fragile;

  let message: string;
  if (ecartVsCible >= 0) {
    message = `+${ecartVsCible.toFixed(1)} pts vs objectif ${metierName} (${seuils.bon}%)`;
  } else if (ecartVsMinimum >= 0) {
    message = `${ecartVsCible.toFixed(1)} pts vs objectif, mais au-dessus du minimum`;
  } else {
    message = `${ecartVsMinimum.toFixed(1)} pts vs minimum ${metierName} (${seuils.fragile}%)`;
  }

  return { niveau, ecartVsCible, ecartVsMinimum, message };
}
